import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import Header from "./Header";
import Greeting from "./Greeting";
import { getMetalRates, formatCurrencyINR } from "../services/metalRates";

// Inline SVG trend icons
const TrendingUpIcon = ({ style }) => (
  <svg
    style={style}
    viewBox="0 0 24 24"
    fill="currentColor"
    width="14"
    height="14"
  >
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
  </svg>
);
const TrendingDownIcon = ({ style }) => (
  <svg
    style={style}
    viewBox="0 0 24 24"
    fill="currentColor"
    width="14"
    height="14"
  >
    <path d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z" />
  </svg>
);

const POLL_MS = 5000;
const MAX_DAYS = 14;
const STORAGE_KEY = "metalRateHistory";
const SNAPSHOT_KEY = "metalRateSnapshot_v2";
const ONE_HOUR_MS = 60 * 60 * 1000;

const getHourlySnapshot = () => {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const updateHourlySnapshot = (data) => {
  const now = Date.now();
  const existing = getHourlySnapshot();
  if (!existing || now - existing.timestamp >= ONE_HOUR_MS) {
    localStorage.setItem(
      SNAPSHOT_KEY,
      JSON.stringify({
        timestamp: now,
        gold: {
          central: data.gold?.central,
          india: data.gold?.india,
          local: data.gold?.local,
        },
        silver: {
          central: data.silver?.central,
          india: data.silver?.india,
          local: data.silver?.local,
        },
        platinum: {
          central: data.platinum?.central,
          india: data.platinum?.india,
          local: data.platinum?.local,
        },
      }),
    );
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [rates, setRates] = useState(null);
  const [hourlyRates, setHourlyRates] = useState(() => getHourlySnapshot());

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    let history = stored ? JSON.parse(stored) : [];
    if (!history.length) {
      const today = new Date();
      for (let i = 13; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateLabel = date.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
        });
        history.push({
          date: date.toISOString(),
          dateLabel,
          gold: 6000 + Math.random() * 500,
          silver: 80 + Math.random() * 20,
          platinum: 25000 + Math.random() * 3000,
        });
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    }
  }, []);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const data = await getMetalRates({ useLiveApi: true });
        setRates(data);

        updateHourlySnapshot(data);
        setHourlyRates(getHourlySnapshot());

        const today = new Date();
        const dateLabel = today.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
        });
        const todayStr = today.toDateString();
        const stored = localStorage.getItem(STORAGE_KEY);
        let history = stored ? JSON.parse(stored) : [];
        const existingIndex = history.findIndex(
          (item) => item.date === todayStr,
        );
        if (existingIndex >= 0) {
          history[existingIndex] = {
            ...history[existingIndex],
            gold: data.gold?.central,
            silver: data.silver?.central,
            platinum: data.platinum?.central,
          };
        } else {
          history.push({
            date: todayStr,
            dateLabel,
            gold: data.gold?.central,
            silver: data.silver?.central,
            platinum: data.platinum?.central,
          });
        }
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(history.slice(-MAX_DAYS)),
        );
      } catch (e) {
        console.error("Failed to fetch rates:", e);
      }
    };

    fetchRates();
    const interval = setInterval(fetchRates, POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const lastUpdated = rates?.updatedAt
    ? new Date(rates.updatedAt).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    : "Loading…";

  const renderChange = (current, previous) => {
    if (!current || !previous) return null;
    const change = ((current - previous) / previous) * 100;
    const changeValue = current - previous;
    const isPositive = change >= 0;
    const color = isPositive ? "#059669" : "#dc2626";
    const bg = isPositive ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)";
    return (
      <span
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 4,
        }}
      >
        {/* Change indicator (icon + percentage) with background pill */}
        <span
          style={{
            color,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {isPositive ? "+" : "-"}
          {formatCurrencyINR(Math.abs(changeValue))}
        </span>
        <span
          className="change"
          style={{
            color,
            background: bg,
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 20,
            width: "fit-content",
          }}
        >
          {isPositive ? (
            <TrendingUpIcon style={{ color }} />
          ) : (
            <TrendingDownIcon style={{ color }} />
          )}
          <span>
            ({isPositive ? "+" : ""}
            {change.toFixed(2)}%)
          </span>
        </span>
        {/* Change value (currency) shown without background, separate from change */}
      </span>
    );
  };

  const rateItems = [
    {
      label: "Gold Market",
      current: rates?.gold?.central,
      previous: hourlyRates?.gold?.central,
      unit: "/g",
      accent: "#d4af37",
    },
    {
      label: "Gold 24K",
      current: rates?.gold?.india,
      previous: hourlyRates?.gold?.india,
      unit: "/g",
      accent: "#b8962e",
    },
    {
      label: "Silver",
      current: rates?.silver?.central,
      previous: hourlyRates?.silver?.central,
      unit: "/g",
      accent: "#9ca3af",
    },
    {
      label: "Platinum",
      current: rates?.platinum?.central,
      previous: hourlyRates?.platinum?.central,
      unit: "/g",
      accent: "#6b7280",
    },
  ];

  return (
    <>
      <Header title="Dashboard" />
      <div className="dashboard">
         <Greeting />

        <section
          className="card live-rate"
          onClick={() => navigate("/liverates")}
          style={{ paddingBottom: 0 }}
        >
          <div className="live-rateheader">
            <span className="live-badge">● Live</span>
            <span className="last-updated">Updated {lastUpdated}</span>
          </div>

          <div className="rate-grid">
            {rateItems.map((item) => (
              <div
                className="rate-item"
                key={item.label}
                style={{ "--item-accent": item.accent }}
              >
                <div className="rate-item-inner">
                  <div className="label">{item.label}</div>
                  {item.current ? (
                    <>
                      <span className="value">
                        {formatCurrencyINR(item.current)}
                        <small>{item.unit}</small>
                      </span>
                      {item.previous &&
                        renderChange(item.current, item.previous)}
                    </>
                  ) : (
                    <span className="value skeleton">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>


        </section>

        <section className="card hero-section gold">
          <div className="hero-content">
            <h2>Shop Gold Jewellery</h2>
            <p>Timeless elegance for every occasion</p>
            <button
              className="btn-explore"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/productSegment/GOLD");
              }}
            >
              Explore Gold
            </button>
          </div>
        </section>

        <div className="thumbnail-grid">
          <div className="goldthumbnail">
            <img
              src={require("../assets/images/products/rings.png")}
              alt="Ring"
              onClick={() => navigate("/productSegment/gold/916huid/gentsring")}
            />
            Rings
          </div>
          <div className="goldthumbnail">
            <img
              src={require("../assets/images/products/necklace.png")}
              alt="Pendant"
              onClick={() =>
                navigate("/productSegment/gold/916huid/longnecklace")
              }
            />
            Necklace
          </div>
          <div className="goldthumbnail">
            <img
              src={require("../assets/images/products/earring.png")}
              alt="Earrings"
              onClick={() => navigate("/productSegment/gold/916huid/earring")}
            />
            Earrings
          </div>
          <div className="goldthumbnail">
            <img
              src={require("../assets/images/products/bangles.png")}
              alt="Bangles"
              onClick={() => navigate("/productSegment/gold/916huid/bangles")}
            />
            Bangles
          </div>
          <div className="goldthumbnail">
            <img
              src={require("../assets/images/products/bracelet.png")}
              alt="Chain"
              onClick={() => navigate("/productSegment/gold/916huid/bracelet")}
            />
            Bracelet
          </div>
        </div>

        <section className="card hero-section silver">
          <div className="hero-content">
            <h2>Trending Silver Styles</h2>
            <p>Contemporary designs for everyday wear</p>
            <button
              className="btn-explore"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/productSegment/SILVER");
              }}
            >
              Explore Silver
            </button>
          </div>
        </section>

        <section className="card popular-items">
          <div className="title-wrapper">
            <span>Popular Items</span>
            <span
              onClick={() => navigate("/productSegment")}
              className="view-all"
            >
              View all
            </span>
          </div>
          <div className="items-grid">
            <div className="item-card">
              <img
                src={require("../assets/images/products/silverPendant.png")}
                alt="Silver Pendant"
              />
              <div className="item-info">
                <div className="item-name">Silver Pendant</div>
                <span className="silverItemprice">₹1,290</span>
              </div>
            </div>
            <div className="item-card">
              <img
                src={require("../assets/images/products/chainBracelet.png")}
                alt="Chain Bracelet"
              />
              <div className="item-info">
                <div className="item-name">Chain Bracelet</div>
                <span className="silverItemprice">₹1,850</span>
              </div>
            </div>
            <div className="item-card">
              <img
                src={require("../assets/images/products/modernEarrings.png")}
                alt="Modern Earrings"
              />
              <div className="item-info">
                <div className="item-name">Modern Earrings</div>
                <span className="silverItemprice">₹990</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Luxury & Industrial Collections Grid (Balanced 3-Col on PC) ─── */}
        <div className="category-showcase-grid">
          <section
            className="card hero-section diamond"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/DIAMOND");
            }}
          >
            <div className="hero-content">
              <h2>Certified Brilliance</h2>
              <p>Premium diamond collections with certification</p>
              <button className="btn-explore">Explore Diamond</button>
            </div>
          </section>

          <section
            className="card hero-section platinum"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/PLATINUM");
            }}
          >
            <div className="hero-content">
              <h2>Premium Collection</h2>
              <p>Premium Platinum collections with certification</p>
              <button className="btn-explore">Explore Platinum</button>
            </div>
          </section>

          <section
            className="card hero-section gemstone"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/GEMS");
            }}
          >
            <div className="hero-content">
              <h2>Gemstone</h2>
              <p>Premium Gemstone collections with certification</p>
              <button className="btn-explore">Explore Gemstone</button>
            </div>
          </section>

          <section
            className="card hero-section pearls"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/pearl");
            }}
          >
            <div className="hero-content">
              <h2>Pearls</h2>
              <p>Premium Pearl collections with certification</p>
              <button className="btn-explore">Explore Pearls</button>
            </div>
          </section>

          <section
            className="card hero-section decorative"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/decor");
            }}
          >
            <div className="hero-content">
              <h2>Temple &amp; Decorative Pieces</h2>
              <p>Traditional craftsmanship with cultural heritage</p>
              <button className="btn-explore">Explore Decorative</button>
            </div>
          </section>

          <section
            className="card hero-section tools"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/tool");
            }}
          >
            <div className="hero-content">
              <h2>Jewellery Tools &amp; Supplies</h2>
              <p>Professional-grade equipment for artisans</p>
              <button className="btn-explore">Explore Tools</button>
            </div>
          </section>

          <section
            className="card hero-section machinery"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/MACHINE");
            }}
          >
            <div className="hero-content">
              <h2>Professional Machinery</h2>
              <p>Industrial equipment for jewelry manufacturing</p>
              <button className="btn-explore">Explore Machinery</button>
            </div>
          </section>

          <section
            className="card hero-section packaging"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/brand");
            }}
          >
            <div className="hero-content">
              <h2>Packaging</h2>
              <p>Elegant and secure packaging solutions.</p>
              <button className="btn-explore">Explore Packaging</button>
            </div>
          </section>

          <section
            className="card hero-section services"
            onClick={(e) => {
              e.stopPropagation();
              navigate("/productSegment/tech");
            }}
          >
            <div className="hero-content">
              <h2>Professional Services</h2>
              <p>End-to-end digital, operational, and financial solutions</p>
              <button className="btn-explore">Explore Services</button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
